import type {SketchRelationSuppressionTransactionInput} from '../../../../../types/sketch/solver/constraint/SketchRelationStateService';
import {isSketchRelationContainer} from '../SketchRelationContainer';

/**
 * 草图关系状态事务服务。
 *
 * 职责：在同一草图历史事务内修改关系抑制状态、重解完整参数岛并处理失败恢复；
 * 本类不创建关系、不删除关系，也不直接写几何。
 */
export class SketchRelationStateService {
    /**
     * 设置一条真实几何关系的抑制状态。
     *
     * @param input 状态事务输入。
     * @returns 状态已提交或原本相同时返回 true；关系无效或求解拒绝时返回 false。
     * @throws 历史事务提交失败时恢复 before 并重新抛出异常。
     */
    public async setSuppressed(input: SketchRelationSuppressionTransactionInput): Promise<boolean> {
        if (!input.targetEntity || typeof input.targetEntity !== 'object' || !Number.isFinite(input.relationId)) {
            return false;
        }

        const container = Reflect.get(input.targetEntity, 'constraints');

        if (!isSketchRelationContainer(container)) {
            return false;
        }

        const relation = container.getRelation(input.relationId);

        if (!relation || relation.isSuppressed === input.suppressed) {
            return relation !== undefined;
        }

        const historySession = input.beginHistorySession({
            label: input.suppressed ? '抑制草图关系' : '恢复草图关系',
            commandType: input.suppressed
                ? 'sketch.runtime.relation.suppress'
                : 'sketch.runtime.relation.restore',
        });

        if (!historySession) {
            return false;
        }

        const beforeSuppressed = relation.isSuppressed;

        relation.setSuppressed(input.suppressed);

        if (!input.solveParametricRelations()) {
            relation.setSuppressed(beforeSuppressed);
            historySession.cancel();
            input.requestRender?.();
            return false;
        }

        try {
            await historySession.commit();
            input.requestRender?.();
            return true;
        } catch (error) {
            /** commit 失败会由历史会话恢复整组 before；字段回退用于无宿主替身的安全兜底。 */
            relation.setSuppressed(beforeSuppressed);
            input.requestRender?.();
            throw error;
        }
    }
}
